import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth, consumeLogoutReason } from "./AuthContext";

/**
 * PVARA Rubric Screening — Revised 2026.
 * Full v1 parity (roles, search, statuses, CVs, chairperson, CSV) + the
 * revised-criteria layer (before→after bars, rank movement, impact deductions,
 * criterion-level evidence, narration) + light/dark themes.
 * Reads v1 endpoints untouched; writes only pvara_rubric_v2_* via /api/pvara-rubric-v2.
 */

const API_URL = process.env.REACT_APP_API_URL || "";
const serif = { fontFamily: "Georgia, 'Times New Roman', serif" };

const ROLES = [
  { id: "md", title: "Managing Director", short: "MD" },
  { id: "gm_policy_strategy", title: "GM — Policy & Strategy", short: "Policy" },
  { id: "gm_licensing_regulatory_sandbox", title: "GM — Licensing & Reg Sandbox", short: "Licensing" },
  { id: "gm_market_supervision", title: "GM — Market Supervision", short: "Mkt Sup" },
  { id: "gm_enforcement", title: "GM — Enforcement", short: "Enforce" },
  { id: "gm_technology_cyber_risk", title: "GM — Technology & Cyber Risk", short: "Tech" },
  { id: "gm_support_services", title: "GM — Support Services", short: "Support" },
];

const STATUS_OPTIONS = [
  { label: "New", value: "submitted" },
  { label: "Shortlisted", value: "shortlisted" },
  { label: "Contacted", value: "contacted" },
  { label: "Interviewed", value: "interview-complete" },
  { label: "Offered", value: "offer" },
  { label: "Hired", value: "hired" },
  { label: "Rejected", value: "rejected" },
];
const STATUS_BY_VALUE = Object.fromEntries(STATUS_OPTIONS.map(s => [s.value, s]));

const IMPACT_FILTERS = [
  { id: "", label: "All impact" },
  { id: "70", label: "−70%" },
  { id: "40", label: "−40%" },
  { id: "0", label: "Proven" },
  { id: "na", label: "Awaiting" },
];

/* ------------------------------------------------------------- theming ---- */

const THEMES = {
  light: {
    name: "light",
    root: "",
    page: "bg-transparent",
    ink: "text-gray-900", ink2: "text-gray-600", muted: "text-gray-400",
    card: "bg-white", cardBorder: "border-gray-200",
    hair: "border-gray-100", hover: "hover:bg-gray-50/70",
    drawer: "bg-[#fbfaf8]",
    input: "bg-white border-gray-200 text-gray-900 placeholder-gray-400",
    chip: "bg-white text-gray-600 border-gray-200 hover:border-gray-400",
    chipOn: "bg-gray-900 text-white border-gray-900",
    trackBg: "#f0efec", barOld: "#c9c7c0", barNew: "#2563eb",
    subCard: "bg-white border-gray-200",
    narr: "bg-white",
    statusPill: {
      submitted: "bg-gray-100 text-gray-700", shortlisted: "bg-blue-100 text-blue-800",
      contacted: "bg-indigo-100 text-indigo-800", "interview-complete": "bg-purple-100 text-purple-800",
      offer: "bg-amber-100 text-amber-800", hired: "bg-green-100 text-green-800", rejected: "bg-red-100 text-red-800",
    },
  },
  dark: {
    name: "dark",
    root: "bg-[#0e0f12] rounded-3xl px-5 md:px-8 py-6 -mx-2",
    page: "bg-[#0e0f12]",
    ink: "text-[#f0efeb]", ink2: "text-[#b9b8b1]", muted: "text-[#75746d]",
    card: "bg-[#16171c]", cardBorder: "border-white/10",
    hair: "border-white/5", hover: "hover:bg-white/[0.04]",
    drawer: "bg-[#111217]",
    input: "bg-[#1b1c22] border-white/10 text-[#f0efeb] placeholder-[#75746d]",
    chip: "bg-[#1b1c22] text-[#b9b8b1] border-white/10 hover:border-white/30",
    chipOn: "bg-[#f0efeb] text-[#111] border-[#f0efeb]",
    trackBg: "#22232a", barOld: "#4d4e55", barNew: "#4d8ef0",
    subCard: "bg-[#1b1c22] border-white/10",
    narr: "bg-[#16171c]",
    statusPill: {
      submitted: "bg-white/10 text-gray-300", shortlisted: "bg-blue-500/20 text-blue-300",
      contacted: "bg-indigo-500/20 text-indigo-300", "interview-complete": "bg-purple-500/20 text-purple-300",
      offer: "bg-amber-500/20 text-amber-300", hired: "bg-green-500/20 text-green-300", rejected: "bg-red-500/20 text-red-300",
    },
  },
};

/* Global shell overrides — applied via <html data-pvara-theme="dark"> so the
   ENTIRE page (body gradient, glass sidebar, nav) goes dark with the screen.
   Scoped by attribute selector; zero effect in light mode or on other screens. */
const DARK_SHELL_CSS = `
html[data-pvara-theme="dark"] body {
  background: #0a0b0e !important;
}
html[data-pvara-theme="dark"] .glass-sidebar {
  background: rgba(17, 18, 22, 0.96) !important;
  border-right: 1px solid rgba(255,255,255,0.06);
  box-shadow: none !important;
}
html[data-pvara-theme="dark"] .glass-sidebar,
html[data-pvara-theme="dark"] .glass-sidebar button,
html[data-pvara-theme="dark"] .glass-sidebar span,
html[data-pvara-theme="dark"] .glass-sidebar div {
  color: #c9c8c1;
}
html[data-pvara-theme="dark"] .glass-sidebar .text-gray-500,
html[data-pvara-theme="dark"] .glass-sidebar .text-gray-400 {
  color: #75746d !important;
}
html[data-pvara-theme="dark"] .glass-button {
  background: rgba(255,255,255,0.07) !important;
}
html[data-pvara-theme="dark"] .glass-sidebar .text-green-700 {
  color: #7ce3a5 !important;
}
html[data-pvara-theme="dark"] .glass-sidebar img {
  background: #f5f4ef;
  border-radius: 10px;
  padding: 4px 6px;
}
html[data-pvara-theme="dark"] .glass-panel,
html[data-pvara-theme="dark"] .glass-card {
  background: rgba(22,23,28,0.9) !important;
  color: #c9c8c1;
}
/* generic content-area dark: makes Methodology / Audit Log follow the theme */
html[data-pvara-theme="dark"] .bg-white { background-color: #16171c !important; }
html[data-pvara-theme="dark"] .bg-gray-50 { background-color: #1b1c22 !important; }
html[data-pvara-theme="dark"] .bg-gray-100 { background-color: #22232a !important; }
html[data-pvara-theme="dark"] .bg-purple-50 { background-color: rgba(147,51,234,.14) !important; }
html[data-pvara-theme="dark"] .bg-blue-50 { background-color: rgba(59,130,246,.14) !important; }
html[data-pvara-theme="dark"] .bg-green-50 { background-color: rgba(34,197,94,.12) !important; }
html[data-pvara-theme="dark"] .bg-amber-50 { background-color: rgba(245,158,11,.12) !important; }
html[data-pvara-theme="dark"] .bg-red-50 { background-color: rgba(239,68,68,.12) !important; }
html[data-pvara-theme="dark"] .text-gray-900 { color: #eceae4 !important; }
html[data-pvara-theme="dark"] .text-gray-800 { color: #dddbd4 !important; }
html[data-pvara-theme="dark"] .text-gray-700 { color: #c9c8c1 !important; }
html[data-pvara-theme="dark"] .text-gray-600 { color: #a6a59e !important; }
html[data-pvara-theme="dark"] .border-gray-200 { border-color: rgba(255,255,255,.09) !important; }
html[data-pvara-theme="dark"] .border-gray-100 { border-color: rgba(255,255,255,.07) !important; }
html[data-pvara-theme="dark"] .shadow, html[data-pvara-theme="dark"] .shadow-sm,
html[data-pvara-theme="dark"] .shadow-lg, html[data-pvara-theme="dark"] .shadow-2xl { box-shadow: none !important; }
html[data-pvara-theme="dark"] .pvara-sb-toggle {
  background: #1b1c22 !important; color: #c9c8c1 !important; border-color: rgba(255,255,255,.15) !important;
}
`;

/* Inject once at module load + apply the saved theme immediately, so the whole
   app (incl. Methodology / Audit views where this component is unmounted)
   keeps the chosen theme. */
if (typeof document !== "undefined" && !document.getElementById("pvara-v2-shell-css")) {
  const el = document.createElement("style");
  el.id = "pvara-v2-shell-css";
  el.textContent = DARK_SHELL_CSS;
  document.head.appendChild(el);
  try {
    const t = localStorage.getItem("pvara_v2_theme") ||
      (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
    document.documentElement.setAttribute("data-pvara-theme", t);
  } catch (e) { /* ignore */ }
}

function fmtDate(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }); }
  catch { return String(ts).slice(0, 10); }
}

function shortRoleLabel(title, roleId) {
  if (!title) return roleId;
  const t = title
    .replace(/^General Manager\s*[—–-]*\s*/i, "")
    .replace(/^Senior Manager\s*/i, "")
    .replace(/^Manager\s*/i, "")
    .trim()
    .replace(/^\(/, "").replace(/\)$/, "")
    .trim();
  return t || title;
}
function ago(ts) {
  if (!ts) return "";
  try {
    const d = (Date.now() - new Date(ts).getTime()) / 86400000;
    if (d < 1) return "today"; if (d < 2) return "1d ago"; return `${Math.floor(d)}d ago`;
  } catch { return ""; }
}

/* --------------------------------------------------------------- atoms ---- */

function DeductionBadge({ pct, compact }) {
  const base = "inline-flex items-center rounded-full font-semibold whitespace-nowrap " +
    (compact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs");
  if (pct === 70) return <span className={base + " bg-red-600 text-white"}>−70% impact</span>;
  if (pct === 40) return <span className={base + " bg-amber-500 text-white"}>−40% impact</span>;
  if (pct === 0) return <span className={base + " bg-green-600 text-white"}>impact proven</span>;
  return <span className={base + " bg-gray-500/15 text-gray-400 border border-gray-400/30"}>awaiting v2</span>;
}

function ScorePair({ v1, v2, th }) {
  return (
    <div className="w-44">
      <div className="relative h-[9px] mb-[3px]">
        <div className="absolute inset-0 rounded" style={{ background: th.trackBg }} />
        {v1 != null && <div className="absolute left-0 top-0 h-[9px] rounded" style={{ width: `${Math.min(v1, 100)}%`, background: th.barOld }} />}
        <span className={"absolute -right-11 -top-1 text-[11px] tabular-nums w-10 text-right " + th.muted}>{v1 != null ? v1.toFixed(1) : "—"}</span>
      </div>
      <div className="relative h-[9px]">
        <div className="absolute inset-0 rounded" style={{ background: th.trackBg }} />
        {v2 != null && <div className="absolute left-0 top-0 h-[9px] rounded" style={{ width: `${Math.min(v2, 100)}%`, background: th.barNew }} />}
        <span className={"absolute -right-11 -top-1 text-[11px] font-bold tabular-nums w-10 text-right " + th.ink}>{v2 != null ? v2.toFixed(1) : "·"}</span>
      </div>
    </div>
  );
}

function RankDelta({ delta, th }) {
  if (delta == null) return <span className={"text-xs " + th.muted}>—</span>;
  if (delta > 0) return <span className="text-xs font-bold text-green-500">▲{delta}</span>;
  if (delta < 0) return <span className="text-xs font-bold text-red-500">▼{-delta}</span>;
  return <span className={"text-xs " + th.muted}>＝</span>;
}

function CvLink({ href, th, stop }) {
  if (!href) return <span className={"text-xs " + th.muted}>no CV</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" onClick={stop ? (e) => e.stopPropagation() : undefined}
       className={"inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[11px] font-semibold transition-colors " +
         th.chip}>
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" />
      </svg>
      CV
    </a>
  );
}

function StatusSelect({ applicationId, current, onChanged, apiFetch, th, stop }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  async function change(v) {
    if (v === current) return;
    setSaving(true); setErr(null);
    try {
      const res = await apiFetch(`${API_URL}/api/applications/${applicationId}/status`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: v }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onChanged(v);
    } catch (e) { if (e.message !== "session-expired") setErr(e.message); }
    finally { setSaving(false); }
  }
  const pill = th.statusPill[current] || "bg-gray-500/15 text-gray-400";
  return (
    <div onClick={stop ? (e) => e.stopPropagation() : undefined}>
      <select value={current || "submitted"} disabled={saving} onChange={e => change(e.target.value)}
              className={"text-[11px] font-semibold rounded-full px-2 py-1 border-0 ring-1 ring-black/10 cursor-pointer " + pill}>
        {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        {!STATUS_BY_VALUE[current] && current && <option value={current}>{current}</option>}
      </select>
      {saving && <div className={"text-[10px] " + th.muted}>saving…</div>}
      {err && <div className="text-[10px] text-red-500">{err}</div>}
    </div>
  );
}

/* --------------------------------------------------- breakdown (dossier) --- */

function OldNew({ label, oldV, newV, max, th }) {
  return (
    <div className={"rounded-xl border px-3 py-2 " + th.subCard + " " + th.cardBorder}>
      <div className={"text-[10px] font-bold uppercase tracking-wide " + th.muted}>{label}</div>
      <div className={"text-lg font-bold tabular-nums " + th.ink} style={serif}>
        <span className={th.muted + " font-normal"}>{oldV != null ? oldV : "—"} → </span>
        {newV != null ? newV : "—"}
        {max && <span className={"text-xs font-normal " + th.muted}> /{max}</span>}
      </div>
    </div>
  );
}

function BreakdownDrawer({ appId, apiFetch, th, scored }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  async function addNote() {
    const t = noteText.trim();
    if (!t || savingNote) return;
    setSavingNote(true);
    try {
      const res = await apiFetch(`${API_URL}/api/applications/${appId}/notes`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: t }),
      });
      if (res.ok) {
        const d = await res.json().catch(() => ({}));
        const n = d.note || { id: String(Date.now()), text: t, timestamp: new Date().toISOString() };
        setNotes(prev => [...prev, { id: n.id, text: n.text, timestamp: n.timestamp }]);
        setNoteText("");
      }
    } catch { /* keep the text so the user can retry */ }
    finally { setSavingNote(false); }
  }
  useEffect(() => {
    // Don't ask the server for a breakdown that doesn't exist — avoids a
    // console-spamming 404 for candidates not yet scored under the revised rubric.
    if (scored === false) {
      setErr("Not yet scored under the revised rubric — this candidate wasn't part of the processed batch. Scoring is run offline and ingested.");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/pvara-rubric-v2/applications/${appId}/breakdown`);
        if (!res.ok) { if (!cancelled) setErr("Not yet scored under the revised rubric — scoring is run offline and ingested."); return; }
        const d = await res.json();
        if (!cancelled) { setData(d); setNotes(d.notes || []); }
      } catch { if (!cancelled) setErr("Failed to load breakdown."); }
    })();
    return () => { cancelled = true; };
  }, [appId, apiFetch, scored]);

  if (err) return <div className={"px-6 py-4 text-sm italic " + th.muted}>{err}</div>;
  if (!data) return <div className={"px-6 py-4 text-sm " + th.muted}>Loading dossier…</div>;

  const extras = data.extras || {};
  const acad = (data.sections || []).find(s => s.section_id === "academic");
  const exp = (data.sections || []).find(s => s.section_id === "experience");
  const v1 = data.v1 || {};
  const dedPct = extras.evidence_of_impact_deduction_pct;
  const narration = [
    acad ? `Academic: ${acad.score}/${acad.max_marks} under the revised table${v1.academic != null ? ` (was ${v1.academic}/30)` : ""}.` : null,
    dedPct > 0 ? `Impact modifier: −${dedPct}% on the experience score — ${extras.evidence_of_impact_rationale || ""}.`
      : (dedPct === 0 ? `Impact modifier: none — ${extras.evidence_of_impact_rationale || ""}.` : null),
    exp && extras.experience_score_before_deduction != null
      ? `Experience: ${extras.experience_score_before_deduction}/40 → ${exp.score}/40 after the modifier.` : null,
    `Revised CV score: ${data.score}/${data.max} = ${data.percent}%${v1.percent != null ? ` (was ${v1.percent}% of 70)` : ""}. Interview sections excluded.`,
  ].filter(Boolean).join(" ");

  return (
    <div className={"px-5 md:px-6 pb-6 pt-2 " + th.drawer} onClick={e => e.stopPropagation()}>
      {/* headline numbers */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <OldNew label="Academic" oldV={v1.academic} newV={acad ? acad.score : null} max="20 (was 30)" th={th} />
        <OldNew label="Experience" oldV={v1.experience} newV={exp ? exp.score : null} max="40" th={th} />
        <OldNew label="CV score %" oldV={v1.percent} newV={data.percent} th={th} />
        <div className={"rounded-xl border px-3 py-2 flex items-center " + th.subCard + " " + th.cardBorder}>
          <DeductionBadge pct={dedPct} />
        </div>
      </div>

      <p className={"text-[15px] leading-relaxed border-l-[3px] rounded-r-xl px-4 py-3 shadow-sm " + th.narr + " " + th.ink2}
         style={{ ...serif, borderColor: th.barNew }}>
        {narration}
      </p>

      {(data.flags || []).length > 0 && (
        <div className="mt-3 space-y-1">
          {data.flags.map((f, i) => <div key={i} className="text-xs font-semibold text-red-500">⚑ {f}</div>)}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5 mt-4">
        <div>
          <h4 className={"text-sm font-bold mb-2 " + th.ink} style={serif}>Section 1 · Academic — {acad ? `${acad.score}/${acad.max_marks}` : "—"}</h4>
          <div className={"rounded-xl border overflow-hidden " + th.card + " " + th.cardBorder}>
            <table className="w-full text-xs">
              <tbody>
                {(acad?.items || []).map(it => (
                  <tr key={it.item_id} className={"border-b last:border-0 " + th.hair}>
                    <td className={"px-3 py-2 w-1/2 " + th.ink2}>{it.label}</td>
                    <td className={"px-3 py-2 font-bold tabular-nums whitespace-nowrap " + th.ink}>{it.score}/{it.max_marks}</td>
                    <td className={"px-3 py-2 " + th.muted}>{it.evidence || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.assessorNote && (
            <div className={"mt-3 text-[12.5px] italic rounded-xl border px-3.5 py-2.5 " + th.subCard + " " + th.cardBorder + " " + th.ink2}>
              <b className="not-italic" style={serif}>Assessor note.</b> {data.assessorNote}
            </div>
          )}
        </div>

        <div>
          <h4 className={"text-sm font-bold mb-2 " + th.ink} style={serif}>
            Section 2 · Impact evidence — three most recent substantive roles
          </h4>
          {(data.recentRolesImpact || []).length > 0 ? (
            <div className="space-y-2.5">
              {data.recentRolesImpact.map((r, i) => (
                <div key={i} className={"rounded-xl border px-3.5 py-3 " + th.subCard + " " + th.cardBorder}>
                  <div className={"text-xs font-bold " + th.ink}>
                    {r.title} <span className={"font-normal " + th.muted}>— {r.org}{r.period ? ` · ${r.period}` : ""}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-2">
                    <div>
                      <div className={"text-[10px] font-bold uppercase tracking-wide " + th.muted}>
                        Qualitative outcomes · {r.qualitative_outcomes}
                      </div>
                      {(r.qualitative_quotes || []).length > 0
                        ? (r.qualitative_quotes || []).map((q, j) => (
                            <div key={j} className={"text-[11.5px] italic mt-1 pl-2 border-l-2 border-green-500/50 " + th.ink2}>“{q}”</div>))
                        : <div className="text-[11px] italic text-red-500 mt-1">× no attributable outcome statements found</div>}
                    </div>
                    <div>
                      <div className={"text-[10px] font-bold uppercase tracking-wide " + th.muted}>
                        Quantified results · {r.quantitative_outcomes}
                      </div>
                      {(r.quantitative_quotes || []).length > 0
                        ? (r.quantitative_quotes || []).map((q, j) => (
                            <div key={j} className={"text-[11.5px] italic mt-1 pl-2 border-l-2 border-blue-500/60 " + th.ink2}>“{q}”</div>))
                        : <div className="text-[11px] italic text-red-500 mt-1">× no quantified results found</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={"text-xs italic " + th.muted}>No per-role impact evidence stored.</div>
          )}
          {(exp?.items || []).length > 0 && (
            <details className="mt-2.5">
              <summary className="text-[11px] font-semibold cursor-pointer" style={{ color: th.barNew }}>experience criterion detail</summary>
              <div className={"rounded-xl border overflow-hidden mt-1.5 " + th.card + " " + th.cardBorder}>
                <table className="w-full text-xs">
                  <tbody>
                    {exp.items.map(it => (
                      <tr key={it.item_id} className={"border-b last:border-0 " + th.hair}>
                        <td className={"px-3 py-1.5 w-1/2 " + th.ink2}>{it.label}</td>
                        <td className={"px-3 py-1.5 font-bold tabular-nums whitespace-nowrap " + th.ink}>{it.score}/{it.max_marks}</td>
                        <td className={"px-3 py-1.5 " + th.muted}>{it.evidence || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      </div>

      {/* Reviewer notes — e.g. "strong, but better fit for a GM role" */}
      <div className={"mt-4 rounded-xl border p-3.5 " + th.subCard + " " + th.cardBorder}>
        <div className={"text-sm font-bold mb-2 " + th.ink} style={serif}>Reviewer notes</div>
        {notes.length > 0 ? (
          <div className="space-y-1.5 mb-2.5">
            {notes.map((n, i) => (
              <div key={n.id || i} className={"text-[12.5px] rounded-lg px-3 py-2 " + th.card + " " + th.cardBorder + " border " + th.ink2}>
                {n.text}
                {n.timestamp && <span className={"block text-[10px] mt-0.5 " + th.muted}>{fmtDate(n.timestamp)}</span>}
              </div>
            ))}
          </div>
        ) : <div className={"text-[11px] italic mb-2 " + th.muted}>No notes yet.</div>}
        <div className="flex items-start gap-2">
          <textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={2}
                    onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") addNote(); }}
                    placeholder="Add a note (e.g. applied for MD — stronger fit for GM). ⌘/Ctrl+Enter to save."
                    className={"flex-1 rounded-lg border px-3 py-2 text-[12.5px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 " + th.input} />
          <button onClick={addNote} disabled={!noteText.trim() || savingNote}
                  className="px-3.5 py-2 rounded-lg text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 whitespace-nowrap">
            {savingNote ? "saving…" : "add note"}
          </button>
        </div>
      </div>

      <div className={"text-[10px] mt-3 " + th.muted}>
        Evaluated {data.evaluatedAt || "—"} · revised grand total out of {data.grandTotalMax || 90} · scoring runs offline (no third-party keys on this server) and is ingested with full evidence.
      </div>
    </div>
  );
}

/* -------------------------------------------------------- criteria strip --- */

const ACADEMIC_DIFF = [
  ["Master's (HEC-recognised)", "8 (field-match tiers)", "4 flat baseline"],
  ["Above baseline (PhD / MPhil / 2nd Master's-MBA)", "4 (tiered)", "4 — highest only, no stacking"],
  ["Foreign degree (QS 2026)", "6 (top-25 = 6 … 101-200 = 1)", "8 — but 1-10 = 8, 11-50 = 2, 51-100 = 1"],
  ["Professional certifications", "6 (3+ certs)", "2 capped — no multi-cert bonus"],
  ["Specialised training", "4", "removed"],
  ["Academic distinctions", "2", "2 unchanged"],
];

function CriteriaStrip({ th }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={"rounded-2xl border px-5 py-3.5 mb-5 " + th.card + " " + th.cardBorder}>
      <div className="flex items-center justify-between gap-4">
        <p className={"text-[13px] leading-snug " + th.ink2}>
          <b className={th.ink} style={serif}>Revised 2026 criteria.</b>&nbsp; Academic <b className={th.ink}>20</b> (was 30) +
          Experience <b className={th.ink}>40</b> = CV-scoreable <b className={th.ink}>60</b> · Sections 3 &amp; 4 interview-only ·
          an <b className={th.ink}>evidence-of-impact deduction</b> (−70% / −40% / 0) applies to the whole experience score.
        </p>
        <button onClick={() => setOpen(!open)} className="text-xs font-bold whitespace-nowrap hover:underline" style={{ color: th.barNew }}>
          {open ? "hide detail" : "what changed?"}
        </button>
      </div>
      {open && (
        <div className="grid lg:grid-cols-2 gap-4 mt-4">
          <div>
            <div className={"text-xs font-bold mb-1.5 " + th.ink} style={serif}>Section 1 — Academic: 30 → 20, item by item</div>
            <div className={"rounded-xl border overflow-hidden " + th.subCard + " " + th.cardBorder}>
              <table className="w-full text-[11.5px]">
                <thead><tr className={th.muted}>
                  <th className="text-left px-3 py-1.5 font-semibold">Criterion</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Old</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Revised</th>
                </tr></thead>
                <tbody>
                  {ACADEMIC_DIFF.map((r, i) => (
                    <tr key={i} className={"border-t " + th.hair}>
                      <td className={"px-3 py-1.5 " + th.ink2}>{r[0]}</td>
                      <td className={"px-3 py-1.5 " + th.muted}>{r[1]}</td>
                      <td className={"px-3 py-1.5 font-semibold " + th.ink}>{r[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <div className={"text-xs font-bold mb-1.5 " + th.ink} style={serif}>Evidence-of-impact deduction — new, on the whole experience score</div>
            <div className={"rounded-xl border px-3.5 py-3 text-[12px] space-y-1.5 " + th.subCard + " " + th.cardBorder + " " + th.ink2}>
              <div><DeductionBadge pct={70} compact /> &nbsp;any of the 3 most recent substantive roles has <b>zero</b> qualitative outcomes</div>
              <div><DeductionBadge pct={40} compact /> &nbsp;outcomes in all roles, but quantified results in fewer than two</div>
              <div><DeductionBadge pct={0} compact /> &nbsp;outcomes in all roles and quantified results in ≥ 2</div>
              <div className={"pt-1 text-[11px] " + th.muted}>
                A result must be attributable to the candidate and describe an outcome — activities, responsibilities and
                employer prestige don't count. Manual-review flags (&lt;3 substantive roles, ambiguous qualifications,
                unparseable CV) never auto-reject. Formula: (Academic + Experience×(1−deduction)) ÷ 60 × 100.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------- chairperson view --- */

function ChairpersonView({ apiFetch, onStatusChanged, th }) {
  const [groups, setGroups] = useState(null);
  const [v2map, setV2map] = useState(new Map());
  const [err, setErr] = useState("");
  const [openCard, setOpenCard] = useState(null);

  const load = useCallback(async (force) => {
    try {
      const url = `${API_URL}/api/pvara-rubric/chairman-shortlist${force ? "?force_refresh=true" : ""}`;
      const [res, res2] = await Promise.all([
        apiFetch(url),
        apiFetch(`${API_URL}/api/pvara-rubric-v2/applications`),
      ]);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setGroups(d.roles || d.groups || []);
      if (res2.ok) {
        const d2 = await res2.json();
        setV2map(new Map((d2.applications || []).map(a => [a.applicationId, a])));
      }
    } catch (e) { if (e.message !== "session-expired") setErr(e.message || "load failed"); }
  }, [apiFetch]);

  useEffect(() => { load(false); }, [load]);

  if (err) return <div className="text-sm text-red-500 py-6">{err}</div>;
  if (!groups) return <div className={"text-sm py-10 text-center " + th.muted}>Preparing the chairperson brief…</div>;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-4">
        <p className={"text-sm " + th.ink2}>
          Every advanced candidate across all roles, ranked within role. <b className={th.ink}>Click a card</b> for the
          full dossier — criterion scores, quoted evidence, narration.
        </p>
        <button onClick={() => load(true)} className="text-xs font-bold hover:underline" style={{ color: th.barNew }}>refresh summaries</button>
      </div>
      {groups.map(g => (
        <section key={g.roleId} className="mb-8">
          <div className="flex items-baseline gap-3 mb-3">
            <h3 className={"text-xl font-bold " + th.ink} style={serif}>{g.roleTitle || g.roleId}</h3>
            <span className={"text-[10px] font-bold px-2 py-0.5 rounded-full " + th.chipOn}>{g.tier}</span>
            <span className={"text-xs " + th.muted}>{g.count} candidate{g.count === 1 ? "" : "s"}</span>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {(g.rows || []).map(row => {
              const v2 = v2map.get(row._id);
              const isOpen = openCard === row._id;
              const v1pct = row.percent != null ? row.percent
                : (row.score != null && row.max ? Math.round(1000 * row.score / row.max) / 10 : null);
              return (
                <div key={row._id}
                     onClick={() => setOpenCard(isOpen ? null : row._id)}
                     className={"rounded-2xl border p-4 shadow-sm transition-all cursor-pointer " +
                       th.card + " " + th.cardBorder + " " +
                       (isOpen ? "md:col-span-2 ring-2 ring-blue-500/40" : "hover:shadow-md hover:-translate-y-[1px]")}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className={"text-[11px] font-bold tabular-nums " + th.muted}>#{row.rank}</span>
                        <span className={"font-bold truncate " + th.ink} style={serif}>{row.name}</span>
                        <span className={"text-[10px] " + th.muted}>{isOpen ? "▴ close" : "▾ dossier"}</span>
                      </div>
                      {(row.currentPosition || row.organisation) && (
                        <div className={"text-xs mt-0.5 truncate " + th.ink2}>
                          {row.currentPosition}{row.organisation ? ` · ${row.organisation}` : ""}
                        </div>
                      )}
                      {row.education && <div className={"text-[11px] mt-0.5 truncate " + th.muted}>{row.education}</div>}
                    </div>
                    <CvLink href={row.cv} th={th} stop />
                  </div>
                  <div className="flex items-end justify-between mt-3 gap-3">
                    <ScorePair v1={v1pct} v2={v2 ? v2.v2Percent : null} th={th} />
                    <div className="flex flex-col items-end gap-1.5">
                      <DeductionBadge pct={v2 ? v2.impactDeductionPct : null} compact />
                      <StatusSelect applicationId={row._id} current={row.status} apiFetch={apiFetch} th={th} stop
                                    onChanged={() => onStatusChanged && onStatusChanged()} />
                    </div>
                  </div>
                  {(row.redFlags || []).length > 0 && !isOpen && (
                    <div className="mt-2 text-[11px] text-red-500">⚑ {row.redFlags[0]}{row.redFlags.length > 1 ? ` (+${row.redFlags.length - 1})` : ""}</div>
                  )}
                  {isOpen && (
                    <div className={"mt-4 -mx-4 -mb-4 rounded-b-2xl border-t " + th.hair}>
                      <BreakdownDrawer appId={row._id} apiFetch={apiFetch} th={th} scored={!!(v2 && v2.v2Percent != null)} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------ login gate --- */

function LoginGateV2({ th }) {
  const { login, verify2fa, resendOtp } = useAuth();
  const [stage, setStage] = useState("password");   // "password" | "otp"
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [userId, setUserId] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resent, setResent] = useState("");
  const [err, setErr] = useState(null);
  const [logoutReason] = useState(() => { try { return consumeLogoutReason(); } catch { return null; } });

  async function onPassword(e) {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      const res = await login({ username, password });
      if (res && res.requires2FA) {           // OTP-enabled account → step 2
        setUserId(res.userId); setStage("otp"); setSubmitting(false); return;
      }
      if (!res || res.ok === false) { setErr((res && res.message) || "Login failed"); setSubmitting(false); return; }
      window.location.reload();
    } catch (e2) { setErr(e2.message || "Login failed"); setSubmitting(false); }
  }

  async function onOtp(e) {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      const res = await verify2fa({ userId, otpCode: otpCode.trim() });
      if (!res || res.ok === false) { setErr((res && res.message) || "Invalid code"); setSubmitting(false); return; }
      window.location.reload();
    } catch (e2) { setErr(e2.message || "Verification failed"); setSubmitting(false); }
  }

  async function onResend() {
    setErr(null); setResent("");
    const res = await resendOtp(userId);
    setResent(res.ok ? "A new code has been sent." : (res.message || "Could not resend code."));
  }

  const inputCls = "w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 " + th.input;

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className={"w-full max-w-md rounded-2xl border p-8 shadow-xl " + th.card + " " + th.cardBorder}>
        <div className="text-center mb-6">
          <div className="text-2xl font-bold" style={{ ...serif, color: th.barNew }}>PVARA</div>
          <div className={"text-[11px] tracking-widest uppercase mt-1 " + th.muted}>Rubric Screening · Revised 2026 · Staff Login</div>
        </div>
        {logoutReason && stage === "password" && (
          <div className="text-xs rounded-lg border border-amber-400/40 bg-amber-500/10 text-amber-600 px-3 py-2 mb-4">{logoutReason}</div>
        )}

        {stage === "password" ? (
          <form onSubmit={onPassword} className="space-y-4">
            <div>
              <label className={"block text-xs font-semibold mb-1 " + th.ink2}>Username</label>
              <input type="text" autoFocus required value={username} onChange={e => setUsername(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={"block text-xs font-semibold mb-1 " + th.ink2}>Password</label>
              <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className={inputCls} />
            </div>
            {err && <div className="text-xs rounded-lg border border-red-400/40 bg-red-500/10 text-red-500 px-3 py-2">{err}</div>}
            <button type="submit" disabled={submitting || !username || !password}
                    className="w-full py-2.5 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 transition">
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
        ) : (
          <form onSubmit={onOtp} className="space-y-4">
            <p className={"text-xs " + th.ink2}>
              A 6-digit verification code was emailed to the account. Enter it below to finish signing in.
            </p>
            <div>
              <label className={"block text-xs font-semibold mb-1 " + th.ink2}>Verification code</label>
              <input type="text" inputMode="numeric" autoFocus required value={otpCode} maxLength={6}
                     onChange={e => setOtpCode(e.target.value.replace(/[^0-9]/g, ""))}
                     className={inputCls + " tracking-[0.4em] text-center text-lg"} placeholder="••••••" />
            </div>
            {resent && <div className="text-xs rounded-lg border border-blue-400/40 bg-blue-500/10 text-blue-500 px-3 py-2">{resent}</div>}
            {err && <div className="text-xs rounded-lg border border-red-400/40 bg-red-500/10 text-red-500 px-3 py-2">{err}</div>}
            <button type="submit" disabled={submitting || otpCode.length < 6}
                    className="w-full py-2.5 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 transition">
              {submitting ? "Verifying…" : "Verify & sign in"}
            </button>
            <div className="flex items-center justify-between text-xs">
              <button type="button" onClick={() => { setStage("password"); setOtpCode(""); setErr(null); setResent(""); }}
                      className={"hover:underline " + th.muted}>← back</button>
              <button type="button" onClick={onResend} className="font-semibold hover:underline" style={{ color: th.barNew }}>resend code</button>
            </div>
          </form>
        )}
        <p className={"text-center text-[10px] mt-5 " + th.muted}>CONFIDENTIAL · FOR OFFICIAL USE ONLY</p>
      </div>
    </div>
  );
}

/* --------------------------------------------------------- main screen ---- */

export default function PvaraRubricViewV2() {
  const { apiFetch, user, logout } = useAuth();

  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("pvara_v2_theme") ||
        (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
    } catch { return "light"; }
  });
  const th = THEMES[theme] || THEMES.light;
  function toggleTheme() {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    try { localStorage.setItem("pvara_v2_theme", next); } catch { /* ignore */ }
  }

  /* drive the WHOLE page with the theme; persists across views (no cleanup) */
  useEffect(() => {
    document.documentElement.setAttribute("data-pvara-theme", theme);
  }, [theme]);

  const [mode, setMode] = useState("screening");
  const [activeGroup, setActiveGroup] = useState("MD");
  const [activeRole, setActiveRole] = useState("md");
  const [groups, setGroups] = useState(null);
  const [counts, setCounts] = useState({});
  const [items, setItems] = useState([]);
  const [v2rows, setV2rows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [impactFilter, setImpactFilter] = useState("");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [sortBy, setSortBy] = useState("revised");
  const [openRow, setOpenRow] = useState(null);
  const [notice, setNotice] = useState("");
  const [reloadTick, setReloadTick] = useState(0);
  const searchRef = useRef(null);
  const reload = useCallback(() => setReloadTick(t => t + 1), []);

  /* '/' focuses search */
  useEffect(() => {
    function onKey(e) {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA" && document.activeElement?.tagName !== "SELECT") {
        e.preventDefault(); searchRef.current?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* role universe: every position present in the data, grouped MD/GM/SM/Mgr */
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/pvara-rubric-v2/role-groups`);
        if (res.ok) {
          const d = await res.json();
          if (!cancelled && (d.groups || []).length) {
            setGroups(d.groups);
            const cts = {};
            d.groups.forEach(g => (g.roles || []).forEach(r => { cts[r.roleId] = r.count; }));
            setCounts(cts);
            return;
          }
        }
      } catch (_) { /* fall back below */ }
      if (cancelled) return;
      // fallback: static spec roles with count probes
      setGroups([{ groupId: "MD", label: "All roles", total: null,
                   roles: ROLES.map(r => ({ roleId: r.id, title: r.title, count: null, v2Scored: 0 })) }]);
      const out = {};
      for (const r of ROLES) {
        try {
          const res = await apiFetch(`${API_URL}/api/pvara-rubric/applications?role_id=${r.id}&limit=1`);
          if (res.ok) { const d = await res.json(); out[r.id] = (typeof d.total === "number") ? d.total : d.count; }
        } catch (_) { /* ignore */ }
        if (cancelled) return;
      }
      if (!cancelled) setCounts(out);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeTitle = useMemo(() => {
    for (const g of groups || []) for (const r of (g.roles || [])) if (r.roleId === activeRole) return r.title;
    return ROLES.find(r => r.id === activeRole)?.title || activeRole;
  }, [groups, activeRole]);

  /* list + v2 merge */
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        const [res1, res2] = await Promise.all([
          apiFetch(`${API_URL}/api/pvara-rubric/applications?role_id=${activeRole}&limit=5000`),
          // per-role fetch: the whole corpus is v2-scored now, so the unfiltered
          // list is ~13k rows and was truncated server-side (under-counted UI)
          apiFetch(`${API_URL}/api/pvara-rubric-v2/applications?role_id=${activeRole}_v2`),
        ]);
        if (res1.status === 403) throw new Error("This account doesn't have screening access (admin / hr / recruiter role required).");
        if (!res1.ok) throw new Error(`HTTP ${res1.status}`);
        const d1 = await res1.json();
        const d2 = res2.ok ? await res2.json() : { applications: [] };
        if (!cancelled) { setItems(d1.items || []); setV2rows(d2.applications || []); }
      } catch (e) { if (!cancelled && e.message !== "session-expired") setError(e.message || "load failed"); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [activeRole, reloadTick, apiFetch]);

  // NOTE: no auto-refetch on window focus / visibility change. Opening a
  // candidate's CV in a new tab and returning used to refire the fetch, which
  // collapsed the open dossier and reset scroll — annoying while reviewing.
  // Use the manual refresh (or role switch) to pull others' changes.

  const v2map = useMemo(() => new Map(v2rows.map(a => [a.applicationId, a])), [v2rows]);

  /* status counts for the chips (within active role, pre-status-filter) */
  const statusCounts = useMemo(() => {
    const out = {};
    items.forEach(r => { out[r.status] = (out[r.status] || 0) + 1; });
    return out;
  }, [items]);

  const merged = useMemo(() => {
    let rows = items.map(it => ({ ...it, v2: v2map.get(it._id) || null }));
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter(r =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.email || "").toLowerCase().includes(q) ||
      (r.cnic || "").toLowerCase().includes(q));
    if (statusFilter) rows = rows.filter(r => r.status === statusFilter);
    if (impactFilter) {
      rows = rows.filter(r => {
        const p = r.v2 ? r.v2.impactDeductionPct : null;
        if (impactFilter === "na") return p == null;
        return String(p) === impactFilter;
      });
    }
    if (flaggedOnly) rows = rows.filter(r => (r.flags || []).length > 0 || (r.v2 && (r.v2.v2Flags || []).length > 0));
    const by = {
      revised: (a, b) => ((b.v2?.v2Percent ?? -1) - (a.v2?.v2Percent ?? -1)) || ((b.percent || 0) - (a.percent || 0)),
      current: (a, b) => (b.percent || 0) - (a.percent || 0),
      name: (a, b) => (a.name || "").localeCompare(b.name || ""),
      applied: (a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0),
    };
    rows.sort(by[sortBy] || by.revised);
    return rows;
  }, [items, v2map, search, statusFilter, impactFilter, flaggedOnly, sortBy]);

  const rankDelta = useMemo(() => {
    const both = merged.filter(r => r.v2 && r.v2.v2Percent != null && r.percent != null);
    const o = new Map([...both].sort((a, b) => b.percent - a.percent).map((r, i) => [r._id, i + 1]));
    const n = new Map([...both].sort((a, b) => b.v2.v2Percent - a.v2.v2Percent).map((r, i) => [r._id, i + 1]));
    const out = new Map();
    both.forEach(r => out.set(r._id, o.get(r._id) - n.get(r._id)));
    return out;
  }, [merged]);

  function exportCsv() {
    const head = ["Rank", "Name", "Email", "Phone", "CNIC", "Degree", "Years", "Status",
      "Current % (/70)", "Revised % (/60)", "Impact deduction", "Flags", "Applied"];
    const esc = v => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [head.join(",")];
    merged.forEach((r, i) => lines.push([
      i + 1, esc(r.name), esc(r.email), esc(r.phone), esc(r.cnic), esc(r.degree), r.experienceYears ?? "",
      esc(STATUS_BY_VALUE[r.status]?.label || r.status || ""),
      r.percent ?? "", r.v2?.v2Percent ?? "",
      r.v2?.impactDeductionPct != null ? `-${r.v2.impactDeductionPct}%` : "",
      esc([...(r.flags || []), ...((r.v2 && r.v2.v2Flags) || [])].join(" | ")),
      fmtDate(r.submittedAt),
    ].join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `pvara-${activeRole}-revised-screening.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  }

  const scoredCount = merged.filter(r => r.v2 && r.v2.v2Percent != null).length;
  const anyFilter = search || statusFilter || impactFilter || flaggedOnly;

  /* not signed in → themed staff login (mirrors the original screen's gate) */
  if (!user) {
    return (
      <div className={"max-w-[1200px] mx-auto pb-16 transition-colors " + th.root}>
        <LoginGateV2 th={th} />
      </div>
    );
  }

  return (
    <div className={"max-w-[1200px] mx-auto pb-16 transition-colors " + th.root}>
      {/* masthead */}
      <div className="flex flex-wrap items-end justify-between gap-4 pt-1 pb-4">
        <div>
          <h1 className={"text-[28px] leading-tight font-bold " + th.ink} style={serif}>
            PVARA Rubric Screening
            <span className="ml-3 align-middle text-[11px] font-sans font-bold tracking-wide text-white bg-blue-700 rounded-full px-2.5 py-1">REVISED 2026</span>
          </h1>
          <p className={"text-[13px] mt-1 " + th.muted}>
            Revised form — Academic 20 + Experience 40, evidence-of-impact weighted. Previous scores stay visible (grey), never modified.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {user && (
            <div className={"flex items-center gap-2 pl-3 pr-1.5 py-1 rounded-xl border text-xs " + th.chip}>
              <svg className="w-3.5 h-3.5 opacity-60" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M19 21v-2a4 4 0 00-4-4H9a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
              </svg>
              <span className="font-semibold max-w-[120px] truncate">{user.name || user.username || "signed in"}</span>
              <button
                onClick={() => { try { logout(); } catch (e) { /* ignore */ } window.location.reload(); }}
                title="Sign out"
                className="ml-1 px-2 py-1 rounded-lg font-bold text-red-500 hover:bg-red-500/10 transition-colors">
                Sign out
              </button>
            </div>
          )}
          <button onClick={toggleTheme} title="toggle light/dark"
                  className={"w-10 h-10 rounded-xl border flex items-center justify-center " + th.chip}>
            {theme === "dark"
              ? <svg className="w-4.5 h-4.5 w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="4"/><path d="M12 2v2m0 16v2M4.9 4.9l1.4 1.4m11.4 11.4l1.4 1.4M2 12h2m16 0h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>
              : <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 12.8A9 9 0 1111.2 3 7 7 0 0021 12.8z"/></svg>}
          </button>
          <div className={"inline-flex rounded-xl border overflow-hidden text-sm font-semibold " + th.cardBorder}>
            <button onClick={() => setMode("screening")}
              className={"px-4 py-2 " + (mode === "screening" ? th.chipOn : th.chip + " border-0")}>
              Per-Role Review
            </button>
            <button onClick={() => setMode("chairperson")}
              className={"px-4 py-2 border-l " + th.cardBorder + " " + (mode === "chairperson" ? th.chipOn : th.chip + " border-0 border-l")}>
              Chairperson View
            </button>
          </div>
          {mode === "screening" && (
            <button onClick={exportCsv} className={"px-3.5 py-2 rounded-xl border text-sm font-semibold " + th.chip}>↓ CSV</button>
          )}
        </div>
      </div>

      <CriteriaStrip th={th} />

      {mode === "chairperson" ? (
        <ChairpersonView apiFetch={apiFetch} onStatusChanged={reload} th={th} />
      ) : (
        <>
          {/* role rail — group tabs, then every position within the group */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {(groups || []).map(g => (
              <button key={g.groupId}
                onClick={() => {
                  setActiveGroup(g.groupId);
                  if (!(g.roles || []).some(r => r.roleId === activeRole) && g.roles && g.roles[0]) {
                    setActiveRole(g.roles[0].roleId); setOpenRow(null);
                  }
                }}
                className={"px-4 py-2 rounded-xl text-sm font-bold border transition-colors " +
                  (activeGroup === g.groupId ? th.chipOn : th.chip)}>
                {g.label}
                <span className="ml-2 text-[11px] font-semibold tabular-nums opacity-60">
                  {(g.roles || []).length > 1 ? `${g.roles.length} roles · ` : ""}{g.total != null ? g.total : ""}
                </span>
              </button>
            ))}
          </div>
          {(() => {
            const g = (groups || []).find(x => x.groupId === activeGroup);
            if (!g || !(g.roles || []).length) return null;
            return (
              <div className="flex flex-wrap items-center gap-1.5 mb-4">
                {g.roles.map(r => (
                  <button key={r.roleId} onClick={() => { setActiveRole(r.roleId); setOpenRow(null); }}
                    className={"px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border transition-colors " +
                      (activeRole === r.roleId ? th.chipOn : th.chip)}>
                    {shortRoleLabel(r.title, r.roleId)}
                    <span className="ml-1.5 text-[10.5px] tabular-nums opacity-60">{r.count != null ? r.count : (counts[r.roleId] ?? "")}</span>
                    {r.v2Scored > 0 && <span className="ml-1 text-[9px] align-middle" style={{ color: th.barNew }}>●{r.v2Scored}</span>}
                  </button>
                ))}
              </div>
            );
          })()}

          {/* command bar */}
          <div className={"rounded-2xl border p-3 mb-4 space-y-2.5 " + th.card + " " + th.cardBorder}>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[260px]">
                <svg className={"w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 " + th.muted} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>
                <input ref={searchRef} value={search} onChange={e => setSearch(e.target.value)}
                       placeholder={`Search ${counts[activeRole] ?? ""} candidates — name, email, CNIC  ( / )`}
                       className={"w-full pl-10 pr-9 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 " + th.input} />
                {search && (
                  <button onClick={() => setSearch("")} className={"absolute right-3 top-1/2 -translate-y-1/2 text-sm " + th.muted}>✕</button>
                )}
              </div>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                      className={"px-3 py-2.5 rounded-xl border text-sm " + th.input}>
                <option value="revised">Sort · Revised %</option>
                <option value="current">Sort · Current %</option>
                <option value="name">Sort · Name</option>
                <option value="applied">Sort · Applied</option>
              </select>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <button onClick={() => setStatusFilter("")}
                className={"px-2.5 py-1 rounded-full text-[11px] font-semibold border " + (statusFilter === "" ? th.chipOn : th.chip)}>
                All {items.length ? <span className="opacity-60 tabular-nums">{items.length}</span> : null}
              </button>
              {STATUS_OPTIONS.map(s => (
                <button key={s.value} onClick={() => setStatusFilter(statusFilter === s.value ? "" : s.value)}
                  className={"px-2.5 py-1 rounded-full text-[11px] font-semibold border " + (statusFilter === s.value ? th.chipOn : th.chip)}>
                  {s.label} {statusCounts[s.value] ? <span className="opacity-60 tabular-nums">{statusCounts[s.value]}</span> : null}
                </button>
              ))}
              <span className={"mx-1 " + th.muted}>·</span>
              {IMPACT_FILTERS.map(f => (
                <button key={f.id} onClick={() => setImpactFilter(impactFilter === f.id ? "" : f.id)}
                  className={"px-2.5 py-1 rounded-full text-[11px] font-semibold border " + ((impactFilter === f.id && f.id !== "") || (f.id === "" && impactFilter === "") ? th.chipOn : th.chip)}>
                  {f.label}
                </button>
              ))}
              <span className={"mx-1 " + th.muted}>·</span>
              <button onClick={() => setFlaggedOnly(!flaggedOnly)}
                className={"px-2.5 py-1 rounded-full text-[11px] font-semibold border " + (flaggedOnly ? "bg-red-600 text-white border-red-600" : th.chip)}>
                ⚑ flagged
              </button>
              {anyFilter && (
                <button onClick={() => { setSearch(""); setStatusFilter(""); setImpactFilter(""); setFlaggedOnly(false); }}
                        className={"ml-auto text-[11px] font-semibold hover:underline " + th.muted}>
                  clear all
                </button>
              )}
            </div>
          </div>

          {notice && <div className="mb-3 text-[13px] rounded-xl border border-amber-400/40 bg-amber-500/10 text-amber-600 px-4 py-2.5">{notice}</div>}
          {error && <div className="mb-3 text-sm text-red-500 font-medium">{error}</div>}

          <div className={"text-xs mb-2 " + th.muted}>
            {activeTitle} · showing <b className={th.ink2}>{merged.length}</b>
            {scoredCount > 0 && <> · <b style={{ color: th.barNew }}>{scoredCount}</b> scored under the revised rubric</>}
          </div>

          {/* list */}
          <div className={"rounded-2xl border shadow-sm overflow-hidden " + th.card + " " + th.cardBorder}>
            <div className={"hidden md:grid grid-cols-[44px_36px_minmax(220px,1.4fr)_120px_230px_120px_140px_110px] gap-2 px-4 py-2.5 text-[10.5px] font-bold uppercase tracking-wide border-b " + th.muted + " " + th.hair}>
              <div>#</div><div>Δ</div><div>Candidate</div><div>CV / Applied</div>
              <div>Current → Revised</div><div>Impact</div><div>Status</div><div className="text-right">Dossier</div>
            </div>

            {loading && <div className={"px-4 py-12 text-center text-sm " + th.muted}>Loading candidates…</div>}
            {!loading && merged.length === 0 && (
              <div className={"px-4 py-12 text-center text-sm " + th.muted}>No candidates match{anyFilter ? " these filters" : ""}.</div>
            )}

            {!loading && merged.map((r, i) => {
              const v2 = r.v2;
              const isOpen = openRow === r._id;
              const flagsCount = (r.flags || []).length + ((v2 && v2.v2Flags) || []).length;
              return (
                <div key={r._id} className={"border-b last:border-0 " + th.hair + " " + (isOpen ? th.drawer : th.hover)}>
                  <div className="grid md:grid-cols-[44px_36px_minmax(220px,1.4fr)_120px_230px_120px_140px_110px] grid-cols-1 gap-2 px-4 py-3 items-center cursor-pointer"
                       onClick={() => setOpenRow(isOpen ? null : r._id)}>
                    <div className={"text-sm font-bold tabular-nums " + th.muted}>{i + 1}</div>
                    <div><RankDelta delta={rankDelta.get(r._id)} th={th} /></div>

                    <div className="min-w-0">
                      <span className={"font-bold " + th.ink} style={serif}>{r.name || "—"}</span>
                      {flagsCount > 0 && <span className="ml-2 text-[10px] font-bold text-red-500">⚑{flagsCount}</span>}
                      <div className={"text-[11px] truncate " + th.muted}>
                        {r.degree || "—"} · {r.experienceYears != null ? `${r.experienceYears}y` : "—"} · {r.cnic || ""}
                      </div>
                      <div className={"text-[11px] truncate " + th.muted}>{r.email || ""}{r.phone ? ` · ${r.phone}` : ""}</div>
                    </div>

                    <div onClick={e => e.stopPropagation()}>
                      <CvLink href={r.cv} th={th} />
                      <div className={"text-[10px] mt-1 " + th.muted}>{fmtDate(r.submittedAt)}</div>
                    </div>

                    <div className="pr-8"><ScorePair v1={r.percent} v2={v2 ? v2.v2Percent : null} th={th} /></div>

                    <div><DeductionBadge pct={v2 ? v2.impactDeductionPct : null} compact /></div>

                    <div>
                      <StatusSelect applicationId={r._id} current={r.status} apiFetch={apiFetch} th={th} stop
                                    onChanged={() => reload()} />
                      {r.lastStatusChange?.byName && (
                        <div className={"text-[10px] mt-0.5 " + th.muted}>by {r.lastStatusChange.byName} · {ago(r.lastStatusChange.at)}</div>
                      )}
                    </div>

                    <div className="text-right">
                      <span className="text-xs font-bold" style={{ color: th.barNew }}>{isOpen ? "close ▴" : "open ▾"}</span>
                    </div>
                  </div>
                  {isOpen && <BreakdownDrawer appId={r._id} apiFetch={apiFetch} th={th} scored={!!(v2 && v2.v2Percent != null)} />}
                </div>
              );
            })}
          </div>

          <p className={"text-[11px] mt-3 " + th.muted}>
            Grey bar = current rubric (% of 70) · blue = revised (% of 60) · Δ = rank movement between the two ·
            revised scoring runs offline and is ingested with evidence — nothing here modifies the current rubric's scores.
          </p>
        </>
      )}

      <p className={"text-center text-[10px] tracking-wide mt-10 " + th.muted}>
        PVARA RUBRIC SCREENING · CONFIDENTIAL · FOR OFFICIAL USE ONLY
      </p>
    </div>
  );
}
